import React, { createContext, FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  visitIQAiPrompt,
  visitIQGeoFilters,
  visitIQGlobalSearchOrg,
  visitIQTaxonomyFilters,
  visitIQTaxonomyFiltersOperators,
} from '~/constants';
import { useSignalContext } from '~/contexts/SignalContext';
import useAudienceFilters from '~/hooks/useAudienceFilters';
import {
  CustomFieldType,
  LogicalOperator,
  PersonCompoundFilter,
  PersonFilter,
  PersonFilterInput,
} from '~/types/api.graphql';
import {
  AudienceCondition,
  FieldCategory,
  FieldType as FieldTypeGeneral,
  FilterConfiguration as FilterConfigurationGeneral,
  FilterExtendedStringField,
  FilterField as FilterFieldGeneral,
  PeopleSnapshot,
  usePeopleSnapshotQuery,
} from '~/types/api.graphql';
import {
  createGeoFilterHelper,
  createGeoFilterHelperFromGraphQL,
  createParentGeoFilterHelperFromGraphQL,
  flattenGeoFilters,
} from '~/utils/filters';
import { buildGeoFiltersFromSnapshot } from '~/utils/geoFilterSnapshot';
import { omitTypename } from '~/utils/helpers';
import PersonFilterHelper from '~/views/audiences/AudiencePage/Tabs/OverviewPage/helpers';
import { pulseTopicToGraphQLFilter } from '~/views/audiences/NewPulseAudience/utils/graphql';

import { useAudienceContext } from './AudienceContext';

type Nullable<T> = T | null;

export type CustomFilterConfiguration = {
  icon: ReactNode;
  readonly?: boolean;
  answer?: string;
  supportedConditions?: string[];
  customField?: string;
};

interface FilterProviderState {
  selectedFilters?: PersonFilterWithoutGeo[];
}

enum FilterFieldCustom {
  Audience = 'audience',
}

type FieldType = FieldTypeGeneral;
type FilterField = FilterFieldGeneral | FilterExtendedStringField | FilterFieldCustom;
type FilterConfiguration = FilterConfigurationGeneral & {
  customFilterConfiguration?: CustomFilterConfiguration;
};

export interface Filter {
  type: FieldType;
  filterField?: FilterField;
  isCustom?: boolean;
  category: FieldCategory;
  title: string;
  configuration?: FilterConfiguration;
}

export enum GeoFilterType {
  Polygon = 'polygon',
  Circle = 'circle',
  List = 'list',
}

export interface GeoFilter<T = any> {
  type: GeoFilterType;
  id: number;
  count?: number;
  zipCodes?: { zip: string; count: number }[];
  excludedZipCodes?: string[];
  isParent?: boolean;
  parentId?: number;
  negate?: boolean;
  data: T;
}

export interface CompoundFilter {
  operator: LogicalOperator;
  filters: (PersonFilter | CompoundFilter)[];
}

interface FilterContextProps {
  filters: Nullable<Filter[]>;
  loadingFilters: boolean;
  loadingPeopleSnapshotData: boolean;
  peopleSnapshotData: PeopleSnapshot;
  getSelectedFiltersCount: () => number;
  hasSelectedTaxonomyFilters: boolean;
  hasSelectedFilters: boolean;
  geoFilters: GeoFilter[];
  addGeoFilter: (geoFilter: GeoFilter) => void;
  updateGeoFilter: (filterId: number, geoFilter: GeoFilter, hitEndpoint?: boolean) => void;
  removeGeoFilter: (geoFilter: GeoFilter) => void;
  filtersQueryParam: () => PersonFilterInput[];
  parentAudienceId: string;
  audienceFilters: PersonCompoundFilter[];
  hasAudienceFilters: boolean;
  clearGlobalAudienceCreationLocalStorage: () => void;
  hasSelectedB2BFilters: boolean;
  compoundFilters: PersonCompoundFilter[];
  setCompoundFilters: (filters: PersonCompoundFilter[]) => void;
  setFiltersLogicalOperator: (operator: LogicalOperator) => void;
  filtersLogicalOperator: LogicalOperator;
  processFilters: (inputFilters: PersonFilter[]) => { filters: PersonCompoundFilter[]; operator: LogicalOperator };
  cleanFilters: (filters: PersonFilter[]) => PersonFilter[];
  aiPrompt: string;
  setAiPrompt: (prompt: string) => void;
}

interface FilterProviderProps {
  children: ReactNode;
  source: string;
}

export const FilterContext = createContext<FilterContextProps>({
  filters: null,
  loadingFilters: false,
  loadingPeopleSnapshotData: false,
  peopleSnapshotData: null,
  getSelectedFiltersCount: null,
  hasSelectedTaxonomyFilters: false,
  hasSelectedFilters: false,
  geoFilters: [],
  addGeoFilter: null,
  updateGeoFilter: null,
  removeGeoFilter: null,
  filtersQueryParam: null,
  parentAudienceId: null,
  audienceFilters: null,
  hasAudienceFilters: false,
  clearGlobalAudienceCreationLocalStorage: null,
  hasSelectedB2BFilters: false,
  compoundFilters: [],
  setCompoundFilters: null,
  setFiltersLogicalOperator: null,
  filtersLogicalOperator: LogicalOperator.And,
  processFilters: null,
  cleanFilters: null,
  aiPrompt: null,
  setAiPrompt: null,
});

export type PersonFilterWithoutGeo = Omit<PersonFilter, 'geoFilter' | 'compoundFilter'>;

export const emptyFilter = {
  operator: LogicalOperator.And,
  filters: [
    {
      stringFilter: {
        field: null,
        operator: null,
      },
    },
  ],
} as PersonCompoundFilter;

export type CompoundFilterType = FieldCategory.Person | FieldCategory.Business;

const mapCustomFieldTypeToFieldType: Record<CustomFieldType, FieldTypeGeneral> = {
  [CustomFieldType.Boolean]: FieldTypeGeneral.Boolean,
  [CustomFieldType.Date]: FieldTypeGeneral.Date,
  [CustomFieldType.Integer]: FieldTypeGeneral.Number,
  [CustomFieldType.String]: FieldTypeGeneral.String,
};

export const FilterProvider: FC<FilterProviderProps> = ({ children, source }) => {
  const location = useLocation();
  const [filters, setFilters] = useState<Filter[]>(undefined);
  const [geoFilters, setGeoFilters] = useState<GeoFilter[]>([]);
  const [geoFiltersToLoad, setGeoFiltersToLoad] = useState<GeoFilter[]>([]);
  const [filtersFromAudience, setFiltersFromAudience] = useState<PersonCompoundFilter[]>([]);
  const [peopleSnapshotData, setPeopleSnapshotData] = useState<PeopleSnapshot>(undefined);
  const [aiPrompt, setAiPrompt] = useState('');

  const [filtersLogicalOperator, setFiltersLogicalOperator] = useState<LogicalOperator>(LogicalOperator.And);
  const [compoundFilters, setCompoundFilters] = useState<PersonCompoundFilter[]>([emptyFilter]);

  const [isIniailized, setInitialized] = useState(false);

  const { selectedTopics } = useSignalContext();
  const { loadFilters, loading: loadingFilters, filters: filtersLoaded } = useAudienceFilters();
  const { audienceData } = useAudienceContext();
  const { audienceId: parentAudienceId } = useParams();

  const taxonomyFiltersLocalStorage = parentAudienceId
    ? `${visitIQTaxonomyFilters}-${source}-${parentAudienceId}`
    : `${visitIQTaxonomyFilters}-${source}`;
  const geoFiltersLocalStorage = parentAudienceId
    ? `${visitIQGeoFilters}-${source}-${parentAudienceId}`
    : `${visitIQGeoFilters}-${source}`;
  const taxonomyOperatorsLocalStorage = parentAudienceId
    ? `${visitIQTaxonomyFiltersOperators}-${source}-${parentAudienceId}`
    : `${visitIQTaxonomyFiltersOperators}-${source}`;
  const aiPromptLocalStorage = parentAudienceId
    ? `${visitIQAiPrompt}-${source}-${parentAudienceId}`
    : `${visitIQAiPrompt}-${source}`;

  useEffect(() => {
    void loadFilters();

    const savedGeoFilters: GeoFilter[] = JSON.parse(localStorage.getItem(geoFiltersLocalStorage)) || [];
    if (savedGeoFilters.length) {
      setGeoFiltersToLoad(savedGeoFilters);
      setGeoFilters(savedGeoFilters);
    }

    const state: FilterProviderState = location.state;
    const savedCompoundFilters = JSON.parse(localStorage.getItem(taxonomyFiltersLocalStorage));

    // Start with saved filters from localStorage
    let finalCompoundFilters: PersonCompoundFilter[] = [];

    if (savedCompoundFilters?.length) {
      finalCompoundFilters = savedCompoundFilters as PersonCompoundFilter[];

      const savedOperator = JSON.parse(localStorage.getItem(taxonomyOperatorsLocalStorage));
      setFiltersLogicalOperator((savedOperator as LogicalOperator) || LogicalOperator.And);
    }

    // Add preselected filters from state if they exist and are not already in the finalCompoundFilters

    if (state?.selectedFilters?.length) {
      const validPreselectedFilters = state.selectedFilters.filter(Boolean);
      // Check if the preselected filters are already in the finalCompoundFilters
      // by converting to JSON strings for deep comparison
      const existingFilterStrings = finalCompoundFilters.flatMap((compoundFilter) =>
        compoundFilter.filters.map((filter) => JSON.stringify(cleanFilter(filter))),
      );

      // Only add filters that don't already exist
      const newFilters = validPreselectedFilters.filter(
        (filter) => !existingFilterStrings.includes(JSON.stringify(cleanFilter(filter))),
      );

      if (newFilters.length > 0) {
        // Separate filters that have nested compoundFilter (should become their own groups)
        // from simple filters (should be grouped together with AND)
        const simpleFilters: PersonFilter[] = [];
        const compoundGroups: PersonCompoundFilter[] = [];

        newFilters.forEach((filter) => {
          if ((filter as PersonFilter).compoundFilter) {
            // This filter has a nested compound - extract it as its own group
            const nestedCompound = (filter as PersonFilter).compoundFilter;
            compoundGroups.push({
              operator: nestedCompound.operator,
              filters: nestedCompound.filters,
            });
          } else {
            // Simple filter - add to the AND group
            simpleFilters.push(filter as PersonFilter);
          }
        });

        // Create compound groups from the extracted filters
        const newCompoundFilters: PersonCompoundFilter[] = [];

        // Add simple filters as an AND group if any exist
        if (simpleFilters.length > 0) {
          newCompoundFilters.push({
            operator: LogicalOperator.And,
            filters: simpleFilters,
          });
        }

        // Add extracted compound groups
        newCompoundFilters.push(...compoundGroups);

        // Combine with any existing saved filters
        finalCompoundFilters = [...newCompoundFilters, ...finalCompoundFilters];
      }
    }

    // If we have no filters at all, use the empty filter
    if (finalCompoundFilters.length === 0) {
      finalCompoundFilters = [emptyFilter];
    }

    setCompoundFilters(finalCompoundFilters);

    // Load saved AI prompt
    const savedAiPrompt = localStorage.getItem(aiPromptLocalStorage);
    if (savedAiPrompt) {
      setAiPrompt(savedAiPrompt);
    }

    setInitialized(true);
    // Hydrate this provider once from route state and localStorage on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (filtersLoaded && (parentAudienceId ? !!audienceData : true)) {
      const audienceCondition = audienceData?.audience?.condition || AudienceCondition.Global;

      // Filter out fields that have fieldAudienceTypes restrictions
      const filteredByAudienceType = filtersLoaded.filter((filter) => {
        const fieldAudienceTypes = filter.configuration?.fieldAudienceTypes;
        // If no fieldAudienceTypes, show for all audiences
        if (!fieldAudienceTypes || fieldAudienceTypes.length === 0) {
          return true;
        }
        return fieldAudienceTypes.includes(audienceCondition);
      });

      const customFields = audienceData?.audience?.customFields || [];
      const customAudienceFields = customFields.map((customField) => ({
        type: mapCustomFieldTypeToFieldType[customField.fieldType],
        category: FieldCategory.Person,
        title: customField.name,
        isCustom: true,
        ...(parentAudienceId ? {} : { filterField: null }),
      }));

      setFilters([...filteredByAudienceType, ...customAudienceFields]);
    }
  }, [filtersLoaded, audienceData, parentAudienceId, audienceData?.audience?.customFields]);

  useEffect(() => {
    if (!isIniailized) return;
    localStorage.setItem(taxonomyFiltersLocalStorage, JSON.stringify(compoundFilters));
    localStorage.setItem(taxonomyOperatorsLocalStorage, JSON.stringify(filtersLogicalOperator));
    localStorage.setItem(geoFiltersLocalStorage, JSON.stringify(geoFilters));
    localStorage.setItem(aiPromptLocalStorage, aiPrompt);
  }, [
    aiPrompt,
    aiPromptLocalStorage,
    compoundFilters,
    geoFilters,
    geoFiltersLocalStorage,
    filtersLogicalOperator,
    isIniailized,
    taxonomyFiltersLocalStorage,
    taxonomyOperatorsLocalStorage,
  ]);

  const cleanFilter = useCallback((filter: PersonFilter): PersonFilter | PersonFilter[] => {
    // Base case: if null/undefined or not an object, return as is
    if (!filter || typeof filter !== 'object') {
      return filter;
    }

    // // Handle arrays
    if (Array.isArray(filter)) {
      return filter.map(cleanFilter).filter(Boolean) as PersonFilter[];
    }

    // Create new object without null values and __typename
    const cleaned: any = {};

    for (const [key, value] of Object.entries(filter)) {
      // Skip __typename and null/undefined values
      if (key === '__typename' || value === null || value === undefined) {
        continue;
      }

      // Recursively clean nested objects
      if (typeof value === 'object') {
        const cleanedValue = cleanFilter(value as PersonFilter);
        if (Object.keys(cleanedValue).length > 0) {
          cleaned[key] = cleanedValue;
        }
      } else {
        // Keep all non-null/undefined values, including 0
        cleaned[key] = value;
      }
    }

    return cleaned;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cleanFilters = useCallback(
    (filters: PersonFilter[]): PersonFilter[] => {
      return filters.map(cleanFilter).filter((value) => value !== null && value !== undefined) as PersonFilter[];
    },
    [cleanFilter],
  );

  const processFilters = useCallback(
    (inputFilters: PersonFilter[]): { filters: PersonCompoundFilter[]; operator: LogicalOperator } => {
      const processedFilters: PersonCompoundFilter[] = [];

      // Case 1: Single compound filter at top level
      if (inputFilters.length === 1 && inputFilters[0].compoundFilter) {
        const topFilter = inputFilters[0].compoundFilter;

        // Process each child filter independently
        topFilter.filters.forEach((childFilter) => {
          const cleanedFilter = cleanFilter(childFilter);
          if (!cleanedFilter) return;

          // If child is a compound, split it
          if ((cleanedFilter as PersonFilter).compoundFilter) {
            const nestedFilters = (cleanedFilter as PersonFilter).compoundFilter.filters
              .map((f) => {
                const helper = PersonFilterHelper.createInstance(f);
                return helper ? helper.getFilterInput() : null;
              })
              .filter((f) => Boolean(f) && !f.geoFilter);

            // Create separate compound for nested filters
            processedFilters.push({
              operator: (cleanedFilter as PersonFilter).compoundFilter.operator,
              filters: nestedFilters,
            });
          } else {
            // Create separate compound for individual filter
            const helper = PersonFilterHelper.createInstance(cleanedFilter as PersonFilter);
            if (!helper) return;

            processedFilters.push({
              operator: LogicalOperator.And,
              filters: [helper.getFilterInput()],
            });
          }
        });
      }
      // Case 2: Multiple filters (mix of compounds and individuals)
      else {
        for (const filter of inputFilters) {
          if (filter.compoundFilter) {
            // Process compound's children (splitting nested compounds)
            const cleanedFilters = filter.compoundFilter.filters
              .map((childFilter) => {
                const cleanedFilter = cleanFilter(childFilter);
                if (!cleanedFilter) return null;

                // Split nested compounds
                if ((cleanedFilter as PersonFilter).compoundFilter) {
                  const nestedFilters = (cleanedFilter as PersonFilter).compoundFilter.filters
                    .map((f) => {
                      const helper = PersonFilterHelper.createInstance(f);
                      return helper ? helper.getFilterInput() : null;
                    })
                    .filter((f) => Boolean(f) && !f.geoFilter);
                  return nestedFilters;
                }

                // Process individual filter
                const helper = PersonFilterHelper.createInstance(cleanedFilter as PersonFilter);
                return helper ? helper.getFilterInput() : null;
              })
              .filter((f) => Boolean(f) && !(f as PersonFilter).geoFilter)
              .flat();

            // Keep compound separate with its processed children
            processedFilters.push({
              operator: filter.compoundFilter.operator,
              filters: cleanedFilters,
            });
          } else {
            // Case 3: Individual filter gets its own separate compound
            if (filter?.geoFilter) continue;
            const cleanedFilter = cleanFilter(filter);
            if (!cleanedFilter) continue;

            const helper = PersonFilterHelper.createInstance(cleanedFilter as PersonFilter);
            if (!helper) continue;

            // Create separate compound for individual filter
            processedFilters.push({
              operator: LogicalOperator.And,
              filters: [helper.getFilterInput()],
            });
          }
        }
      }

      return {
        filters: processedFilters,
        operator: inputFilters[0]?.compoundFilter?.operator || LogicalOperator.And,
      };
    },
    [cleanFilter],
  );
  // Load audienceFilters into the selected ones.
  // Guards on both parentAudienceId presence and ID match to avoid loading stale context
  // data from a previously-viewed audience into a fresh creation flow.
  useEffect(() => {
    if (parentAudienceId && audienceData?.audience?.id === parentAudienceId && audienceData?.audience?.filters) {
      let personFilters: PersonFilter[] = [];
      const json = JSON.stringify(audienceData.audience.filters, omitTypename);
      personFilters = JSON.parse(json);
      setFiltersFromAudience(audienceData?.audience?.filters as PersonCompoundFilter[]);

      if (!JSON.parse(localStorage.getItem(geoFiltersLocalStorage))?.length) {
        // geoFilter entries can be nested inside compoundFilter groups (e.g. Pulse audiences),
        // not just at the top level, so flatten before extracting.
        // Editing a Pulse audience (source "pulse") loads the audience's OWN filters, not a
        // parent's, they must stay fully editable/removable, not marked isParent (which is
        // create-segment's "inherited, locked" semantics and hides the edit/delete icons).
        const geoFiltersFromAudience = flattenGeoFilters(personFilters).map((filter) =>
          source === 'pulse'
            ? createGeoFilterHelperFromGraphQL(filter.geoFilter).fromGraphQLGeoFilter()
            : createParentGeoFilterHelperFromGraphQL(filter.geoFilter),
        );
        setGeoFiltersToLoad(geoFiltersFromAudience);
      }
    }
  }, [audienceData, geoFiltersLocalStorage, parentAudienceId]);

  const audienceFilters = useMemo(() => {
    if (parentAudienceId) {
      return filtersFromAudience;
    }
    return null;
  }, [filtersFromAudience, parentAudienceId]);

  const hasAudienceFilters = useMemo(() => {
    return Object.keys(audienceFilters || {}).length > 0;
  }, [audienceFilters]);

  const clearGlobalAudienceCreationLocalStorage = useCallback(() => {
    [
      taxonomyFiltersLocalStorage,
      geoFiltersLocalStorage,
      taxonomyOperatorsLocalStorage,
      aiPromptLocalStorage,
      visitIQGlobalSearchOrg,
    ].forEach((key) => localStorage.removeItem(key));
  }, [aiPromptLocalStorage, geoFiltersLocalStorage, taxonomyFiltersLocalStorage, taxonomyOperatorsLocalStorage]);

  const addGeoFilter = (geoFilter: GeoFilter) => {
    // we should remove excluded zip codes from the parent filters
    // so we remove copied filters
    setGeoFiltersToLoad(geoFilters.filter((f) => !f.parentId).concat(geoFilter));
  };

  const removeGeoFilter = (geoFilter: GeoFilter) => {
    const geoFiltersToLoadTemp = geoFilters.filter((filter) => geoFilter.id !== filter.id);
    setGeoFiltersToLoad(geoFiltersToLoadTemp);
  };

  const updateGeoFilter = (filterId: number, geoFilter: GeoFilter, hitEndpoint = true) => {
    const index = geoFilters.findIndex((filter) => filter.id === filterId);
    if (index < 0) return;

    const geoFiltersTemp = [...geoFilters];
    geoFiltersTemp[index] = geoFilter;
    setGeoFiltersToLoad(geoFiltersTemp);

    if (!hitEndpoint) {
      setGeoFilters(geoFiltersTemp);
    }
  };

  const getValidFiltersArrayFromCompoundFilters = (compoundFilters: PersonCompoundFilter[]) => {
    return compoundFilters
      ?.reduce((filters, actual) => {
        return filters?.concat(actual?.filters);
      }, [])
      .filter((filter) => {
        const personFilterHelper = PersonFilterHelper.createInstance(filter as PersonFilter);
        if (!personFilterHelper) return false;
        return personFilterHelper.isValidFilter();
      });
  };

  const getValidFiltersInsideCompoundFilter = (compoundFilter: PersonFilter | PersonCompoundFilter) => {
    if ((compoundFilter as PersonCompoundFilter)?.filters) {
      const filters = (compoundFilter as PersonCompoundFilter).filters;
      return filters.filter((filter) => {
        const personFilterHelper = PersonFilterHelper.createInstance(filter);
        if (!personFilterHelper) return false;
        return personFilterHelper.isValidFilter();
      });
    }
    return [compoundFilter];
  };

  const getFilterByField = (personFilterHelper, filter: Filter) => {
    return filter.isCustom
      ? filter.title === personFilterHelper.getField()
      : filter.filterField === personFilterHelper.getField();
  };

  const getSelectedFiltersCount = useCallback(() => {
    // Get Filters in the compound filters that are valid
    const filtersFromCompound = getValidFiltersArrayFromCompoundFilters(compoundFilters);

    // Get the count of valid filters for the category
    const count = filtersFromCompound?.reduce((count: number, filter) => {
      const personFilterHelper = PersonFilterHelper.createInstance(filter as PersonFilter);
      return filters?.some((filter) => getFilterByField(personFilterHelper, filter)) ? (count = count + 1) : count;
    }, 0);

    const nonGeoAudienceFiltersCount =
      filtersFromAudience?.filter((f) => !(f as unknown as PersonFilter).geoFilter).length ?? 0;

    if (nonGeoAudienceFiltersCount > 0) {
      return Number(count) + nonGeoAudienceFiltersCount;
    }
    return Number(count);
  }, [compoundFilters, filters, filtersFromAudience]);

  const hasSelectedTaxonomyFilters = useMemo(() => {
    const filtersFromCompound = getValidFiltersArrayFromCompoundFilters(compoundFilters);
    return filtersFromCompound?.some((filter) => {
      const helper = PersonFilterHelper.createInstance(filter as PersonFilter);
      return filters
        ?.filter((filterCat) => filterCat?.category === FieldCategory.Person)
        .some((filter) => getFilterByField(helper, filter));
    });
  }, [compoundFilters, filters]);

  const hasSelectedB2BFilters = useMemo(() => {
    const filtersFromCompound = getValidFiltersArrayFromCompoundFilters(compoundFilters);
    return filtersFromCompound?.some((filter) => {
      const helper = PersonFilterHelper.createInstance(filter as PersonFilter);
      return filters
        ?.filter((filterCat) => filterCat?.category === FieldCategory.Business)
        .some((filter) => getFilterByField(helper, filter));
    });
  }, [compoundFilters, filters]);

  const hasSelectedFilters = useMemo(() => {
    return (
      hasSelectedTaxonomyFilters || hasSelectedB2BFilters || geoFiltersToLoad.some((geoFilter) => !geoFilter.isParent)
    );
  }, [hasSelectedTaxonomyFilters, geoFiltersToLoad, hasSelectedB2BFilters]);

  const compoundFilter = useMemo(() => {
    const getValidFilters = (filters: (PersonFilter | PersonCompoundFilter)[], arrayToPush: PersonFilter[]) => {
      filters.forEach((filter) => {
        const validFilters = getValidFiltersInsideCompoundFilter(filter)
          .map((filter) => {
            const personFilterHelper = PersonFilterHelper.createInstance(filter as PersonFilter);
            return personFilterHelper?.getFilterInput();
          })
          .filter(Boolean);

        if (validFilters?.length > 0) {
          if ((filter as PersonCompoundFilter).operator) {
            arrayToPush.push({
              compoundFilter: {
                operator: (filter as PersonCompoundFilter).operator,
                filters: validFilters,
              },
            });
          } else {
            arrayToPush.push(...validFilters);
          }
        }
      });
    };

    const filtersFromCompound: PersonFilter[] = [];
    getValidFilters(compoundFilters, filtersFromCompound);
    const result = [];

    if (filtersFromCompound.length > 0) {
      result.push({
        compoundFilter: {
          operator: filtersLogicalOperator,
          filters: filtersFromCompound,
        },
      });
    }

    return result;
  }, [compoundFilters, filtersLogicalOperator]);

  const filtersQueryParam = useCallback(
    (skipExcludedZips?: boolean) => {
      return (selectedTopics.length > 0 ? [] : (compoundFilter as PersonFilter[]))
        .concat(
          geoFiltersToLoad
            .filter((geoFilter) => !geoFilter.isParent)
            .map((geoFilter) => createGeoFilterHelper(geoFilter).toGraphQLGeoFilter(skipExcludedZips)),
        )
        .concat(pulseTopicToGraphQLFilter(selectedTopics))
        .filter(Boolean);
    },
    [geoFiltersToLoad, compoundFilter, selectedTopics],
  );

  const {
    loading: loadingPeopleSnapshotData,
    data: peopleSnapshotRaw,
    error: peopleSnapshotError,
  } = usePeopleSnapshotQuery({
    variables: {
      filters: filtersQueryParam(true),
      // Editing a Pulse audience (source "pulse" + a route audienceId) queries availability for
      // the audience's own matching criteria, same as creating one, not people already inside a
      // parent audience, which is what `parentAudienceId` means for create-segment/create-lookalike.
      // Sending it here would make the backend treat the Pulse-topic filter as invalid for a
      // "non-pulse child of this parent" and reject the query.
      ...(parentAudienceId && source !== 'pulse' && { parentAudienceId }),
    },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (peopleSnapshotRaw) {
      setPeopleSnapshotData(peopleSnapshotRaw.peopleSnapshot);
      setGeoFilters(
        buildGeoFiltersFromSnapshot(
          geoFiltersToLoad,
          peopleSnapshotRaw.peopleSnapshot.geoResults,
          peopleSnapshotRaw.peopleSnapshot.postalCodeCounts,
        ),
      );
    }
    // geoFiltersToLoad changes already trigger a new query via filtersQueryParam; only
    // recalculate geoFilters when the snapshot result arrives to avoid a stale-data blink.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peopleSnapshotRaw]);

  useEffect(() => {
    if (peopleSnapshotError) clearGlobalAudienceCreationLocalStorage();
  }, [clearGlobalAudienceCreationLocalStorage, peopleSnapshotError]);

  return (
    <FilterContext.Provider
      value={{
        filters,
        loadingFilters,
        loadingPeopleSnapshotData,
        peopleSnapshotData,
        getSelectedFiltersCount,
        hasSelectedTaxonomyFilters,
        hasSelectedFilters,
        addGeoFilter,
        updateGeoFilter,
        removeGeoFilter,
        geoFilters,
        filtersQueryParam,
        parentAudienceId,
        audienceFilters,
        hasAudienceFilters,
        clearGlobalAudienceCreationLocalStorage,
        hasSelectedB2BFilters,
        compoundFilters,
        setCompoundFilters,
        filtersLogicalOperator,
        processFilters,
        setFiltersLogicalOperator,
        cleanFilters,
        aiPrompt,
        setAiPrompt,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilterContext: () => FilterContextProps = () => {
  return React.useContext(FilterContext);
};
